import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Bell, Check, AlertCircle, CreditCard, Info } from 'lucide-react';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  
  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };
  
  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/unread-count`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };
  
  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/mark-read/${notificationId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  
  const markAllAsRead = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/mark-all-read`, {
        method: 'POST',
        credentials: 'include'
      });
      
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };
  
  const getIcon = (type) => {
    switch (type) {
      case 'payment_expiring':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'payment_expired':
        return <CreditCard className="h-5 w-5 text-red-500" />;
      case 'welcome':
        return <Check className="h-5 w-5 text-green-500" />;
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };
  
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Ahora';
    if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)} h`;
    return date.toLocaleDateString('es-BO');
  };
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-primary text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0 bg-zinc-950 border-zinc-800"
        align="end"
      >
        <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="font-heading text-white uppercase text-sm">Notificaciones</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={markAllAsRead}
              className="text-xs text-primary hover:text-primary/80"
            >
              Marcar todas leídas
            </Button>
          )}
        </div>
        
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-zinc-500">
              No hay notificaciones
            </div>
          ) : (
            notifications.map((notification) => (
              <div 
                key={notification.notification_id}
                className={`p-3 border-b border-zinc-800 hover:bg-zinc-900/50 transition-colors cursor-pointer ${!notification.read ? 'bg-zinc-900/30' : ''}`}
                onClick={() => !notification.read && markAsRead(notification.notification_id)}
                data-testid={`notification-${notification.notification_id}`}
              >
                <div className="flex items-start space-x-3">
                  {getIcon(notification.type)}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${notification.read ? 'text-zinc-400' : 'text-white'}`}>
                      {notification.title}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                      {notification.message}
                    </p>
                    <p className="text-xs text-zinc-600 mt-1">
                      {formatTime(notification.created_at)}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2" />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
